import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, audienceListsApi, campaignsApi, segmentsApi, templatesApi } from "@/api";
import type { SegmentFilter, Channel } from "@/types";
import { CHANNEL_LABELS } from "@/types";
import { ArrowLeft, Save, Send, X, Clock, FileText, Upload, Eye } from "lucide-react";
import { format } from "date-fns";

const traderTypeOptions = [
  "業販・取付", "業販", "一次代理店", "代理店", "ディーラー",
  "取付", "二次代理店", "販社", "0次代理店", "三次代理店",
];
const sourceOptions = [
  { value: "TRADER", label: "取引先" },
  { value: "TRADER_CONTACT", label: "担当者" },
  { value: "MEMBER", label: "会員" },
  { value: "FAX_PROSPECT", label: "FAXリスト" },
];
const traderRankOptions = ["S", "A", "B", "C", "D"];
const rfmRankOptions = ["A", "B", "C", "D"];
const lastOrderOptions = [
  { value: "30", label: "30日以内" },
  { value: "90", label: "90日以内" },
  { value: "180", label: "半年以内" },
  { value: "365", label: "1年以内" },
];
const faxListOptions = [
  { value: "秒速FAX(03)", label: "秒速FAX(03)" },
  { value: "クラコールFAX(03)", label: "クラコールFAX(03)" },
  { value: "秒速FAX(050)", label: "秒速FAX(050)" },
];

function buildPreviewContactFooter() {
  return [
    '<div style="border-top:1px solid #e2e8f0;margin-top:32px;padding-top:16px;font-size:13px;color:#64748b;">',
    '<p style="margin-bottom:12px;"><a href="https://spherelight.s3.ap-northeast-1.amazonaws.com/ma-email/hid-led-flyer-nissan.pdf" style="color:#2563eb;font-weight:bold;">販促チラシ（PDF）をダウンロード</a></p>',
    "<p style=\"margin-bottom:12px;\">印刷済みチラシの送付をご希望の方は営業部までお気軽にご連絡ください。</p>",
    "<p>ご不明な点は担当営業までお気軽にお問い合わせください。</p>",
    '<p style="margin-top:8px;">スフィアライト 営業部<br>',
    'Email: <a href="mailto:sales@sphere-light.com">sales@sphere-light.com</a></p>',
    '<p style="margin-top:12px;font-size:12px;color:#94a3b8;">今後のメール配信を希望されない場合は、<a href="/api/unsubscribe/preview" style="color:#64748b;">配信解除</a>してください。</p>',
    "</div>",
  ].join("");
}

function renderEditorPreviewHtml(html: string) {
  const mergeVars: Record<string, string> = {
    email: "preview@example.invalid",
    lastName: "山田",
    firstName: "太郎",
    company: "株式会社サンプル",
    traderType: "PA",
    traderRank: "A",
    rfmRank: "A",
    unsubscribeUrl: "/api/unsubscribe/preview",
    contactFooter: buildPreviewContactFooter(),
  };
  return html.replace(/\{\{(\w+)\}\}/g, (_, key) => mergeVars[key] ?? "");
}

function CheckboxGroup({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (vals: string[]) => void;
}) {
  const toggle = (val: string) => {
    onChange(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val],
    );
  };

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-2 py-1 rounded text-xs border transition-colors ${
              selected.includes(opt.value)
                ? "bg-blue-500 text-white border-blue-500"
                : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TemplateSelector({ onSelect }: { onSelect: (id: string) => void }) {
  const { data } = useQuery({
    queryKey: ["templates", "newsletter"],
    queryFn: () => templatesApi.list({ kind: "NEWSLETTER" }),
  });

  if (!data?.data.length) return null;

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={16} className="text-slate-500" />
        <h3 className="text-sm font-bold">テンプレートから作成</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {data.data.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => onSelect(tpl.id)}
            className="px-3 py-2 rounded border text-xs text-left hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            {tpl.name}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isNew = !id;

  const { data: auth } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => authApi.me(),
    retry: false,
  });

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [textBody, setTextBody] = useState("");
  const [channel, setChannel] = useState<Channel>("EMAIL");
  const [scheduledAt, setScheduledAt] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [faxDocumentId, setFaxDocumentId] = useState<number | null>(null);
  const [faxUploading, setFaxUploading] = useState(false);
  const faxFileRef = useRef<HTMLInputElement>(null);
  const [faxTemplateKey, setFaxTemplateKey] = useState<string>("");
  const [faxVariantKey, setFaxVariantKey] = useState<string>("");
  const [emailTemplateId, setEmailTemplateId] = useState("");
  const [emailTemplateName, setEmailTemplateName] = useState("");

  // Segment filter state
  const [traderType, setTraderType] = useState<string[]>([]);
  const [audienceListId, setAudienceListId] = useState("");
  const [traderRank, setTraderRank] = useState<string[]>([]);
  const [source, setSource] = useState<string[]>([]);
  const [rfmRank, setRfmRank] = useState<string[]>([]);
  const [company, setCompany] = useState<string[]>([]);
  const [companyContains, setCompanyContains] = useState<string[]>([]);
  const [leadScoreMin, setLeadScoreMin] = useState<string>("");
  const [leadScoreMax, setLeadScoreMax] = useState<string>("");
  const [lastOrderWithin, setLastOrderWithin] = useState<string>("");
  const [lastOrderBefore, setLastOrderBefore] = useState<string>("");
  const [excludeContactIds, setExcludeContactIds] = useState<string[]>([]);
  const [companyNotContains, setCompanyNotContains] = useState<string[]>([]);
  const [hasFaxNumber, setHasFaxNumber] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewContacts, setPreviewContacts] = useState<Array<{ id: string; email: string; lastName?: string; firstName?: string; company?: string; traderType?: string }>>([]);
  const [showRecipients, setShowRecipients] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [selectedTestRecipients, setSelectedTestRecipients] = useState<string[]>([]);
  const [testSendPending, setTestSendPending] = useState(false);
  const [newTestRecipient, setNewTestRecipient] = useState("");
  const [testRecipientError, setTestRecipientError] = useState("");

  // Load existing campaign
  const { data: existing } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => campaignsApi.get(id!),
    enabled: !!id,
  });

  const { data: audienceListsData } = useQuery({
    queryKey: ["audience-lists", "campaign-editor"],
    queryFn: () => audienceListsApi.list({ targetType: "CONTACT" }),
  });

  const { data: testDeliveryOptions } = useQuery({
    queryKey: ["test-delivery-options"],
    queryFn: () => campaignsApi.testDeliveryOptions(),
    enabled: auth?.role === "editor" || auth?.role === "admin",
  });

  const addTestRecipientMutation = useMutation({
    mutationFn: (email: string) => campaignsApi.addTestDeliveryRecipient(email),
    onMutate: () => setTestRecipientError(""),
    onSuccess: (options) => {
      queryClient.setQueryData(["test-delivery-options"], options);
      setSelectedTestRecipients((current) => {
        if (current.includes(options.added) || current.length >= options.maxRecipients) return current;
        return [...current, options.added];
      });
      setNewTestRecipient("");
    },
    onError: (error) => {
      setTestRecipientError(error instanceof Error ? error.message : "テスト配信先を追加できませんでした");
    },
  });

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setSubject(existing.subject);
      setHtmlBody(existing.htmlBody);
      setTextBody(existing.textBody || "");
      setChannel((existing.channel || "EMAIL") as Channel);
      setFaxDocumentId(existing.faxDocumentId ?? null);
      setFaxTemplateKey(existing.faxTemplateKey ?? "");
      setFaxVariantKey(existing.faxVariantKey ?? "");
      setEmailTemplateId(existing.emailTemplateId ?? "");
      setEmailTemplateName(existing.emailTemplate?.name ?? "");
      const sq = existing.segmentQuery as SegmentFilter | null;
      setAudienceListId(existing.audienceListId || sq?.audienceListId || "");
      if (sq) {
        setTraderType(sq.traderType || []);
        setTraderRank(sq.traderRank || []);
        setSource(sq.source || []);
        setRfmRank(sq.rfmRank || []);
        setCompany(sq.company || []);
        setCompanyContains(sq.companyContains || []);
        setLeadScoreMin(sq.leadScoreMin !== undefined ? String(sq.leadScoreMin) : "");
        setLeadScoreMax(sq.leadScoreMax !== undefined ? String(sq.leadScoreMax) : "");
        setLastOrderWithin(sq.lastOrderWithin !== undefined ? String(sq.lastOrderWithin) : "");
        setLastOrderBefore(sq.lastOrderBefore !== undefined ? String(sq.lastOrderBefore) : "");
        setExcludeContactIds(sq.excludeContactIds || []);
        setCompanyNotContains(sq.companyNotContains || []);
        setHasFaxNumber(sq.hasFaxNumber || false);
      }
    }
  }, [existing]);

  // Build segment filter for preview
  const buildFilter = useCallback((): Record<string, string> => {
    const params: Record<string, string> = { channel };
    if (audienceListId) {
      params.audienceListId = audienceListId;
      return params;
    }
    if (traderType.length) params.traderType = traderType.join(",");
    if (traderRank.length) params.traderRank = traderRank.join(",");
    if (source.length) params.source = source.join(",");
    if (rfmRank.length) params.rfmRank = rfmRank.join(",");
    if (company.length) params.company = company.join(",");
    if (leadScoreMin) params.leadScoreMin = leadScoreMin;
    if (leadScoreMax) params.leadScoreMax = leadScoreMax;
    if (lastOrderWithin) params.lastOrderWithin = lastOrderWithin;
    if (lastOrderBefore) params.lastOrderBefore = lastOrderBefore;
    if (hasFaxNumber) params.hasFaxNumber = "true";
    if (companyNotContains.length) params.companyNotContains = companyNotContains.join(",");
    if (companyContains.length) params.companyContains = companyContains.join(",");
    if (excludeContactIds.length) params.excludeContactIds = excludeContactIds.join(",");
    return params;
  }, [channel, audienceListId, traderType, traderRank, source, rfmRank, company, leadScoreMin, leadScoreMax, lastOrderWithin, lastOrderBefore, hasFaxNumber, companyNotContains, companyContains, excludeContactIds]);

  // Preview count
  useEffect(() => {
    const params = buildFilter();
    // Never retain a previous count while a new preview is pending/failed.
    setPreviewCount(null);
    segmentsApi.preview(params).then((r) => setPreviewCount(r.count)).catch(() => setPreviewCount(null));
  }, [buildFilter]);

  const buildSegmentQuery = (): SegmentFilter => {
    const filter: SegmentFilter = {};
    if (audienceListId) {
      filter.audienceListId = audienceListId;
      return filter;
    }
    if (traderType.length) filter.traderType = traderType;
    if (traderRank.length) filter.traderRank = traderRank;
    if (source.length) filter.source = source;
    if (rfmRank.length) filter.rfmRank = rfmRank;
    if (company.length) filter.company = company;
    if (leadScoreMin) filter.leadScoreMin = Number(leadScoreMin);
    if (leadScoreMax) filter.leadScoreMax = Number(leadScoreMax);
    if (lastOrderWithin) filter.lastOrderWithin = Number(lastOrderWithin);
    if (lastOrderBefore) filter.lastOrderBefore = Number(lastOrderBefore);
    if (excludeContactIds.length) filter.excludeContactIds = excludeContactIds;
    if (companyContains.length) filter.companyContains = companyContains;
    if (companyNotContains.length) filter.companyNotContains = companyNotContains;
    if (hasFaxNumber) filter.hasFaxNumber = true;
    return filter;
  };

  const saveMutation = useMutation({
    mutationFn: (data: {
      name: string;
      subject: string;
      htmlBody: string;
      textBody?: string;
      channel?: Channel;
      segmentQuery: SegmentFilter;
      emailTemplateId?: string | null;
      audienceListId?: string | null;
      faxTemplateKey?: string | null;
      faxVariantKey?: string | null;
    }) => {
      if (isNew) return campaignsApi.create(data);
      return campaignsApi.update(id!, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate("/");
    },
  });

  const [sendPending, setSendPending] = useState(false);
  const [schedulePending, setSchedulePending] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: () => campaignsApi.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
    },
  });

  const handleTemplate = async (templateId: string) => {
    try {
      const tpl = await templatesApi.get(templateId);
      setEmailTemplateId(tpl.id);
      setEmailTemplateName(tpl.name);
      setSubject(tpl.subject);
      setHtmlBody(tpl.htmlBody);
      setTextBody(tpl.textBody || "");
      if (!name.trim()) setName(tpl.name);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!isNew) return;
    const listId = searchParams.get("audienceListId");
    const templateId = searchParams.get("templateId");
    if (listId) setAudienceListId(listId);
    if (templateId) void handleTemplate(templateId);
  }, [isNew, searchParams]);

  /** Save current form state and return the campaign (create or update) */
  const saveFirst = async (): Promise<string | null> => {
    if (!name.trim() || !subject.trim()) return null;
    // For non-FAX channels, htmlBody is required
    if (channel !== "FAX" && !htmlBody.trim()) return null;
    const payload = {
      name,
      subject,
      htmlBody: htmlBody || "(FAX campaign)",
      textBody: textBody || undefined,
      channel,
      segmentQuery: buildSegmentQuery(),
      emailTemplateId: emailTemplateId || undefined,
      audienceListId: audienceListId || undefined,
      ...(channel === "FAX"
        ? {
            faxTemplateKey: faxTemplateKey.trim() || null,
            faxVariantKey: faxVariantKey.trim() || null,
          }
        : {}),
    };
    try {
      if (isNew) {
        const created = await campaignsApi.create(payload);
        queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        return created.id;
      }
      await campaignsApi.update(id!, payload);
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
      return id!;
    } catch {
      return null;
    }
  };

  const handleSchedule = async () => {
    if (!scheduledAt || schedulePending || auth?.role !== "admin" || previewCount === null) return;
    const dt = new Date(scheduledAt);
    if (dt <= new Date()) {
      alert("過去の日時は指定できません");
      return;
    }
    const batching = channel === "EMAIL" && previewCount > 1000
      ? `\n1キャンペーンのまま約${Math.ceil(previewCount / 1000)}回に自動分割し、初回30分・2回目以降5分を観察します。異常時は自動停止します。`
      : "";
    if (!confirm(`${format(dt, "yyyy/MM/dd HH:mm")} に${previewCount.toLocaleString()}件を送信予約します。${batching}\nよろしいですか？`)) return;
    setSchedulePending(true);
    try {
      const campaignId = await saveFirst();
      if (!campaignId) return;
      await campaignsApi.schedule(campaignId, dt.toISOString(), previewCount);
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${campaignId}`);
    } finally {
      setSchedulePending(false);
    }
  };

  const handleFaxUpload = async (file: File) => {
    if (!id) return;
    setFaxUploading(true);
    try {
      const result = await campaignsApi.uploadFaxPdf(id, file);
      setFaxDocumentId(result.faxDocumentId);
      queryClient.invalidateQueries({ queryKey: ["campaign", id] });
    } catch (err: any) {
      alert(err.message || "PDF upload failed");
    } finally {
      setFaxUploading(false);
    }
  };

  const handleSave = () => {
    if (!name.trim() || !subject.trim()) return;
    if (channel !== "FAX" && !htmlBody.trim()) return;
    saveMutation.mutate({
      name,
      subject,
      htmlBody: htmlBody || "(FAX campaign)",
      textBody: textBody || undefined,
      channel,
      segmentQuery: buildSegmentQuery(),
      emailTemplateId: emailTemplateId || undefined,
      audienceListId: audienceListId || undefined,
      ...(channel === "FAX"
        ? {
            faxTemplateKey: faxTemplateKey.trim() || null,
            faxVariantKey: faxVariantKey.trim() || null,
          }
        : {}),
    });
  };

  const handleSaveExclusions = async () => {
    const savedId = await saveFirst();
    if (!savedId) return;
    const params = buildFilter();
    const result = await segmentsApi.contacts(params, 1, 1000);
    setPreviewContacts(result.data);
  };

  const handleSend = async () => {
    if (sendPending || auth?.role !== "admin" || previewCount === null || previewCount === 0) return;
    const batching = channel === "EMAIL" && previewCount > 1000
      ? `\n1キャンペーンのまま約${Math.ceil(previewCount / 1000)}回に自動分割し、初回30分・2回目以降5分を観察します。異常時は自動停止します。`
      : "";
    if (!confirm(`${previewCount.toLocaleString()}件の宛先に送信します。${batching}\nよろしいですか？`)) return;
    setSendPending(true);
    try {
      const campaignId = await saveFirst();
      if (!campaignId) return;
      await campaignsApi.send(campaignId, previewCount);
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate("/");
    } finally {
      setSendPending(false);
    }
  };

  const handleTestDelivery = async () => {
    if (testSendPending || channel !== "EMAIL" || selectedTestRecipients.length === 0) return;
    if (!confirm(`${selectedTestRecipients.length}件の許可済み社内宛先へテスト送信します。よろしいですか？`)) return;
    setTestSendPending(true);
    try {
      const campaignId = await saveFirst();
      if (!campaignId) {
        alert("キャンペーンを保存できなかったため、テスト送信を中止しました");
        return;
      }
      const result = await campaignsApi.testDeliveries(campaignId, selectedTestRecipients);
      if (!result.ok) {
        alert(`テスト送信の一部が失敗しました（成功 ${result.sent}/${result.deliveries.length}件）`);
        return;
      }
      alert(`${result.sent}件のテスト送信が完了しました`);
    } catch (err) {
      alert(err instanceof Error ? err.message : "テスト送信に失敗しました");
    } finally {
      setTestSendPending(false);
    }
  };

  const isDraft = !existing || existing.status === "DRAFT";
  const isSending = existing?.status === "SENDING" || existing?.status === "PAUSED";
  const canRelease = auth?.role === "admin" && previewCount !== null && previewCount > 0;
  const selectedAudienceListName = (audienceListsData?.data ?? []).find((list) => list.id === audienceListId)?.name
    ?? existing?.audienceList?.name
    ?? "";

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/")} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-xl font-bold">
          {isNew ? "新規キャンペーン" : `キャンペーン編集`}
        </h2>
        {existing && (
          <span className="text-xs text-slate-400">ID: {existing.id}</span>
        )}
      </div>

      {/* Channel selector */}
      {isDraft && (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <label className="block text-xs font-medium text-slate-600 mb-2">チャネル</label>
          <div className="flex gap-2">
            {(Object.keys(CHANNEL_LABELS) as Channel[]).map((ch) => (
              <button
                key={ch}
                type="button"
                onClick={() => setChannel(ch)}
                className={`px-3 py-1.5 rounded text-sm border transition-colors ${
                  channel === ch
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
                }`}
              >
                {CHANNEL_LABELS[ch]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Template selector (email only) */}
      {isDraft && channel === "EMAIL" && <TemplateSelector onSelect={handleTemplate} />}

      {(emailTemplateId || audienceListId) && (
        <div className="bg-white p-4 rounded-lg shadow-sm border flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="text-slate-500">原稿: </span>
            <span className="font-medium">{emailTemplateName || emailTemplateId || "未選択"}</span>
          </div>
          <div>
            <span className="text-slate-500">宛先リスト: </span>
            <span className="font-medium">{selectedAudienceListName || audienceListId || "未選択"}</span>
          </div>
        </div>
      )}

      {/* Campaign form */}
      <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">キャンペーン名</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="2026年3月キャンペーン"
            className="w-full px-3 py-2 border rounded-md text-sm"
            disabled={!isDraft}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">件名</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="件名を入力"
            className="w-full px-3 py-2 border rounded-md text-sm"
            disabled={!isDraft}
          />
        </div>
        {/* FAX: PDF upload */}
        {channel === "FAX" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">FAX原稿 (PDF)</label>
            {faxDocumentId ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-green-600">PDF アップロード済み (doc #{faxDocumentId})</span>
                {isDraft && (
                  <button
                    type="button"
                    onClick={() => faxFileRef.current?.click()}
                    className="text-xs text-blue-500 hover:text-blue-700"
                  >
                    差し替え
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => faxFileRef.current?.click()}
                  disabled={faxUploading || isNew}
                  className="flex items-center gap-2 px-3 py-2 border rounded-md text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  <Upload size={16} />
                  {faxUploading ? "アップロード中..." : "PDFをアップロード"}
                </button>
                {isNew && (
                  <span className="text-xs text-slate-400">先に下書き保存してからアップロードしてください</span>
                )}
              </div>
            )}
            <input
              ref={faxFileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFaxUpload(file);
                e.target.value = "";
              }}
            />

            {/* DM キャンペーン: per-recipient PDF + QR/検索コード */}
            <div className="mt-4 p-3 border rounded-md bg-slate-50 space-y-3">
              <p className="text-xs text-slate-500">
                DMキャンペーン (per-recipient PDF + QR + 検索コード) を使う場合は下記を指定してください。
                指定すると上の共通PDFは無視され、ictfax の <code>send-custom</code> API が使われます。
              </p>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">テンプレートキー (faxTemplateKey)</label>
                <input
                  type="text"
                  value={faxTemplateKey}
                  onChange={(e) => setFaxTemplateKey(e.target.value)}
                  placeholder="例: fax-dm-pilot-2026-04-A"
                  pattern="[a-zA-Z0-9._\\-]+"
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  disabled={!isDraft}
                />
                <p className="mt-1 text-xs text-slate-400">
                  サーバー上 <code>ma-app/assets/fax-dm/&lt;キー&gt;/</code> に <code>base.pdf</code> + <code>meta.json</code> を配置
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">バリアント識別子 (faxVariantKey, 集計用)</label>
                <input
                  type="text"
                  value={faxVariantKey}
                  onChange={(e) => setFaxVariantKey(e.target.value)}
                  placeholder="例: pilot-A / pilot-B / pilot-C"
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  disabled={!isDraft}
                />
              </div>
            </div>
          </div>
        )}

        {/* EMAIL/SMS/LINE: HTML body */}
        {channel !== "FAX" && (
          <>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">本文 (HTML)</label>
                {htmlBody && (
                  <div className="flex items-center gap-3">
                    {!isNew && (
                      <a
                        href={`/api/campaigns/${id}/preview`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                      >
                        <Eye size={12} />
                        別タブで確認
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      className="text-xs text-blue-500 hover:text-blue-700"
                    >
                      {showPreview ? "コード編集" : "プレビュー"}
                    </button>
                  </div>
                )}
              </div>
              {isDraft && !showPreview && (
                <div className="flex flex-wrap gap-1 mb-1">
                  <span className="text-xs text-slate-400">差し込み:</span>
                  {["lastName", "firstName", "company", "email", "traderType", "traderRank", "rfmRank", "contactFooter"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const tag = `{{${v}}}`;
                        setHtmlBody((prev) => prev + tag);
                      }}
                      className="px-1.5 py-0.5 rounded bg-slate-100 text-xs text-slate-600 hover:bg-slate-200"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              )}
              {showPreview ? (
                <div className="border rounded-md overflow-hidden bg-white" style={{ height: 360 }}>
                  <iframe
                    srcDoc={renderEditorPreviewHtml(htmlBody)}
                    sandbox=""
                    title="HTML preview"
                    className="w-full h-full border-0"
                  />
                </div>
              ) : (
                <textarea
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  rows={12}
                  placeholder="<html><body>...</body></html>"
                  className="w-full px-3 py-2 border rounded-md text-sm font-mono resize-y"
                  disabled={!isDraft}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">テキスト版 (任意)</label>
              <textarea
                value={textBody}
                onChange={(e) => setTextBody(e.target.value)}
                rows={4}
                placeholder="テキスト版の本文"
                className="w-full px-3 py-2 border rounded-md text-sm resize-y"
                disabled={!isDraft}
              />
            </div>
          </>
        )}
      </div>

      {/* Segment builder */}
      <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold">セグメント</h3>
          <div className="text-sm">
            対象: <span className="font-bold text-blue-600">{previewCount ?? "..."}</span> 件
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">保存済み宛先リスト</label>
          <select
            value={audienceListId}
            onChange={(e) => setAudienceListId(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm bg-white"
          >
            <option value="">条件で抽出する</option>
            {(audienceListsData?.data ?? []).map((list) => (
              <option key={list.id} value={list.id}>
                {list.name}（{list.lastCount.toLocaleString()}件）
              </option>
            ))}
          </select>
          {audienceListId && (
            <p className="mt-1 text-xs text-slate-500">
              このキャンペーンは保存済み宛先リストを参照します。追加・除外は宛先リスト画面で管理します。
            </p>
          )}
        </div>

        <div className={audienceListId ? "opacity-40 pointer-events-none space-y-4" : "space-y-4"}>
          <CheckboxGroup
            label="取引先タイプ"
            options={traderTypeOptions.map((v) => ({ value: v, label: v }))}
            selected={traderType}
            onChange={setTraderType}
          />
          <CheckboxGroup
            label="取引先ランク"
            options={traderRankOptions.map((v) => ({ value: v, label: v }))}
            selected={traderRank}
            onChange={setTraderRank}
          />
          <CheckboxGroup
            label="ソース"
            options={sourceOptions}
            selected={source}
            onChange={setSource}
          />
          <CheckboxGroup
            label="RFMランク"
            options={rfmRankOptions.map((v) => ({ value: v, label: v }))}
            selected={rfmRank}
            onChange={setRfmRank}
          />

        {/* Lead score range */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">リードスコア</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={leadScoreMin}
              onChange={(e) => setLeadScoreMin(e.target.value)}
              placeholder="最小"
              className="w-24 px-2 py-1 border rounded text-sm"
              min={0}
            />
            <span className="text-slate-400">〜</span>
            <input
              type="number"
              value={leadScoreMax}
              onChange={(e) => setLeadScoreMax(e.target.value)}
              placeholder="最大"
              className="w-24 px-2 py-1 border rounded text-sm"
              min={0}
            />
          </div>
        </div>

        {/* Last order within */}
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">最終注文</label>
          <div className="flex flex-wrap gap-2">
            {lastOrderOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLastOrderWithin(lastOrderWithin === opt.value ? "" : opt.value)}
                className={`px-2 py-1 rounded text-xs border transition-colors ${
                  lastOrderWithin === opt.value
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-slate-600 border-slate-300 hover:border-blue-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Has FAX number toggle */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hasFaxNumber}
              onChange={(e) => setHasFaxNumber(e.target.checked)}
              className="rounded border-slate-300"
            />
            <span className="text-xs font-medium text-slate-600">FAX番号あり</span>
          </label>
        </div>

        {channel === "FAX" && (
          <CheckboxGroup
            label="FAXリスト"
            options={faxListOptions}
            selected={company}
            onChange={setCompany}
          />
        )}

        {/* Company exclusion (read-only display) */}
        {companyNotContains.length > 0 && (
          <div className="text-xs text-slate-600 bg-red-50 border border-red-200 rounded p-2">
            <span className="font-medium text-red-700">除外: </span>
            {companyNotContains.join(", ")}
          </div>
        )}
        {companyContains.length > 0 && (
          <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-2">
            <span className="font-medium text-blue-700">会社名を含む: </span>
            {companyContains.join(", ")}
          </div>
        )}
        </div>

        {/* Recipient preview */}
        <div className="pt-2 border-t">
          <button
            type="button"
            onClick={async () => {
              setShowRecipients(!showRecipients);
              if (!showRecipients) {
                const params = buildFilter();
                const result = await segmentsApi.contacts(params, 1, 1000);
                setPreviewContacts(result.data);
              }
            }}
            className="text-xs text-blue-500 hover:text-blue-700"
          >
            {showRecipients ? "宛先を閉じる" : "宛先を確認"}
          </button>
          {showRecipients && previewContacts.length > 0 && (() => {
            const q = recipientSearch.toLowerCase();
            const filtered = q
              ? previewContacts.filter((c) =>
                  (c.company || "").toLowerCase().includes(q) ||
                  (c.lastName || "").toLowerCase().includes(q) ||
                  (c.firstName || "").toLowerCase().includes(q) ||
                  c.email.toLowerCase().includes(q))
              : previewContacts;
            return (
              <div className="mt-2">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="text"
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    placeholder="会社名・名前・メールで検索"
                    className="flex-1 px-2 py-1 border rounded text-xs"
                  />
                  <span className="text-xs text-slate-500">
                    {filtered.length}件表示{excludeContactIds.length > 0 && <span className="text-red-500 ml-1">（{excludeContactIds.length}件除外中）</span>}
                  </span>
                  {excludeContactIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSaveExclusions}
                      disabled={saveMutation.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                    >
                      <Save size={12} />
                      {saveMutation.isPending ? "保存中" : "リストから除外して保存"}
                    </button>
                  )}
                </div>
                {excludeContactIds.length > 0 && (
                  <p className="mb-2 text-xs text-red-600">
                    チェックした宛先は、保存後にこのキャンペーンの配信対象リストから消えます。
                  </p>
                )}
                <div className="max-h-96 overflow-y-auto border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 w-8">除外</th>
                        <th className="px-2 py-1 text-left">会社名</th>
                        <th className="px-2 py-1 text-left">名前</th>
                        <th className="px-2 py-1 text-left">メール</th>
                        <th className="px-2 py-1 text-left">タイプ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c) => {
                        const excluded = excludeContactIds.includes(c.id);
                        return (
                          <tr key={c.id} className={`border-t hover:bg-slate-50 ${excluded ? "bg-red-50 line-through opacity-60" : ""}`}>
                            <td className="px-2 py-1 text-center">
                              <input
                                type="checkbox"
                                checked={excluded}
                                onChange={() => {
                                  setExcludeContactIds((prev) =>
                                    excluded ? prev.filter((id) => id !== c.id) : [...prev, c.id],
                                  );
                                }}
                                className="rounded border-slate-300"
                              />
                            </td>
                            <td className="px-2 py-1">{c.company || "-"}</td>
                            <td className="px-2 py-1">{[c.lastName, c.firstName].filter(Boolean).join(" ") || "-"}</td>
                            <td className="px-2 py-1 text-slate-500">{c.email}</td>
                            <td className="px-2 py-1 text-slate-400">{c.traderType || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {isDraft && channel === "EMAIL" && (auth?.role === "editor" || auth?.role === "admin") && (
        <div className="bg-amber-50 p-5 rounded-lg border border-amber-200 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-amber-900">テスト送信</h3>
            <p className="mt-1 text-xs text-amber-800">
              通常の配信対象や予約経路は使わず、サーバーで許可された社内宛先だけへ送信します。
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-64 flex-1">
              <span className="block text-xs font-medium text-amber-900 mb-1">テスト配信先を追加</span>
              <input
                type="email"
                value={newTestRecipient}
                onChange={(event) => setNewTestRecipient(event.target.value)}
                placeholder="name@spread-inc.co.jp"
                className="w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={() => addTestRecipientMutation.mutate(newTestRecipient.trim())}
              disabled={addTestRecipientMutation.isPending || !newTestRecipient.trim()}
              className="rounded border border-amber-500 bg-white px-4 py-2 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            >
              {addTestRecipientMutation.isPending ? "追加中..." : "追加"}
            </button>
          </div>
          {testDeliveryOptions?.allowedDomains?.length ? (
            <p className="text-xs text-amber-700">
              追加可能なドメイン: {testDeliveryOptions.allowedDomains.map((domain) => `@${domain}`).join(", ")}
            </p>
          ) : null}
          {testRecipientError ? <p className="text-sm text-red-700">{testRecipientError}</p> : null}
          {testDeliveryOptions?.configured && testDeliveryOptions.recipients.length > 0 ? (
            <>
              <div className="flex flex-wrap gap-2">
                {testDeliveryOptions.recipients.map((recipient) => {
                  const checked = selectedTestRecipients.includes(recipient);
                  return (
                    <label key={recipient} className="flex items-center gap-2 px-3 py-2 bg-white border border-amber-300 rounded text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked && selectedTestRecipients.length >= testDeliveryOptions.maxRecipients}
                        onChange={() => setSelectedTestRecipients((current) =>
                          checked ? current.filter((value) => value !== recipient) : [...current, recipient]
                        )}
                      />
                      {recipient}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-amber-700">選択上限: {testDeliveryOptions.maxRecipients}件</p>
              <button
                type="button"
                onClick={handleTestDelivery}
                disabled={testSendPending || selectedTestRecipients.length === 0 || !name.trim() || !subject.trim() || !htmlBody.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700 disabled:opacity-50"
              >
                <Send size={16} />
                {testSendPending ? "保存・テスト送信中..." : "選択先へテスト送信"}
              </button>
            </>
          ) : (
            <p className="text-sm text-red-700">許可済みテスト宛先が未設定のため送信できません。</p>
          )}
        </div>
      )}

      {/* Actions */}
      {isDraft && channel === "EMAIL" && previewCount !== null && previewCount > 1000 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
          リスト分割は不要です。1キャンペーンのまま約{Math.ceil(previewCount / 1000)}回に自動分割し、
          初回30分・2回目以降5分を観察して、バウンス・苦情・拒否などの異常時は残りを自動停止します。
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-end">
        {isDraft && (
          <>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending || !name.trim() || !subject.trim() || (channel !== "FAX" && !htmlBody.trim())}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              <Save size={16} />
              {saveMutation.isPending ? "保存中..." : "下書き保存"}
            </button>
            {!isNew && auth?.role === "admin" && (
              <>
                <button
                  onClick={handleSend}
                  disabled={sendPending || !canRelease}
                  title={previewCount === null ? "プレビュー件数を取得できるまで送信できません" : undefined}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
                >
                  <Send size={16} />
                  {sendPending ? "保存・送信中..." : "今すぐ送信"}
                </button>
                <button
                  onClick={() => setShowSchedule(!showSchedule)}
                  disabled={previewCount === null}
                  title={previewCount === null ? "プレビュー件数を取得できるまで予約できません" : undefined}
                  className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-slate-50"
                >
                  <Clock size={16} />
                  予約送信
                </button>
              </>
            )}
          </>
        )}
        {isSending && (
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
          >
            <X size={16} />
            {cancelMutation.isPending ? "キャンセル中..." : "送信キャンセル"}
          </button>
        )}
        {existing?.status === "SCHEDULED" && (
          <button
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 disabled:opacity-50"
          >
            <X size={16} />
            {cancelMutation.isPending ? "キャンセル中..." : "予約キャンセル"}
          </button>
        )}
      </div>

      {/* Schedule picker */}
      {showSchedule && isDraft && !isNew && auth?.role === "admin" && (
        <div className="bg-white p-4 rounded-lg shadow-sm border flex items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">送信予約日時</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
              className="px-3 py-2 border rounded-md text-sm"
            />
          </div>
          <button
            onClick={handleSchedule}
            disabled={schedulePending || !scheduledAt || !canRelease}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
          >
            <Clock size={16} />
            {schedulePending ? "保存・予約中..." : "予約確定"}
          </button>
        </div>
      )}

      {/* Stats for sent campaigns */}
      {existing?.stats && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-sm font-bold mb-3">送信結果</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{existing.totalSent}</p>
              <p className="text-xs text-slate-500">送信</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{existing.stats.opened}</p>
              <p className="text-xs text-slate-500">開封</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{existing.stats.clicked}</p>
              <p className="text-xs text-slate-500">クリック</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{existing.totalBounced}</p>
              <p className="text-xs text-slate-500">バウンス</p>
            </div>
          </div>
        </div>
      )}

      {(saveMutation.isError || cancelMutation.isError) && (
        <p className="text-sm text-red-600">
          {(saveMutation.error || cancelMutation.error)?.message}
        </p>
      )}
    </div>
  );
}
